/**
 * Eligibility rule for the in-app newsletter prompt.
 * Shown to signed-in, email-verified users whose newsletter status is empty or
 * "cold". Never shown to people who unsubscribed or have an unusable address.
 */
export const NEWSLETTER_PROMPT_SNOOZE_DAYS = 14;
export const NEWSLETTER_PROMPT_MAX_DISMISSALS = 3;

const ELIGIBLE_STATUSES = new Set<string | null>([null, '', 'cold']);

export interface NewsletterPromptInput {
  emailVerified: boolean;
  massMailStatus: string | null;
  dismissedAt: string | null;
  dismissCount: number;
  optinRequestedAt: string | null;
  now?: Date;
}

export function shouldShowNewsletterPrompt(input: NewsletterPromptInput): boolean {
  if (!input.emailVerified) return false;
  if (input.optinRequestedAt) return false;
  if (!ELIGIBLE_STATUSES.has(input.massMailStatus)) return false;
  if (input.dismissCount >= NEWSLETTER_PROMPT_MAX_DISMISSALS) return false;
  if (input.dismissedAt) {
    const now = (input.now ?? new Date()).getTime();
    const snoozeMs = NEWSLETTER_PROMPT_SNOOZE_DAYS * 24 * 60 * 60 * 1000;
    if (now - new Date(input.dismissedAt).getTime() < snoozeMs) return false;
  }
  return true;
}

/**
 * In-article subscribe box: a voluntary, explicit action, so people who
 * unsubscribed earlier may see it too. Hidden for current subscribers,
 * unusable addresses (bounced/junk) and pending opt-ins.
 */
const IN_ARTICLE_STATUSES = new Set<string | null>([null, '', 'cold', 'unsubscribed']);

export interface InArticleBoxInput {
  massMailStatus: string | null;
  optinRequestedAt: string | null;
}

export function shouldShowInArticleNewsletterBox(input: InArticleBoxInput): boolean {
  if (input.optinRequestedAt) return false;
  return IN_ARTICLE_STATUSES.has(input.massMailStatus);
}
